import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface MemeShareButtonsProps {
  title: string;
  memeId: number;
  compact?: boolean;
}

export default function MemeShareButtons({ title, memeId, compact = false }: MemeShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/memes#meme-${memeId}`;
  const shareText = `😹 "${title}" — Check out this hilarious cat meme on MeowCentral!`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const shareLinks = [
    {
      id: 'twitter',
      label: 'X / Twitter',
      icon: 'ri-twitter-x-line',
      color: 'bg-black hover:bg-gray-800',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'bg-[#1877F2] hover:bg-[#166fe5]',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'reddit',
      label: 'Reddit',
      icon: 'ri-reddit-fill',
      color: 'bg-[#FF4500] hover:bg-[#e03d00]',
      url: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`,
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      color: 'bg-[#25D366] hover:bg-[#1ebe5a]',
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-1.5">
        {shareLinks.map((s) => (
          <a
            key={s.id}
            href={s.url}
            target="_blank"
            rel="nofollow noopener noreferrer"
            title={`Share on ${s.label}`}
            className={`w-7 h-7 flex items-center justify-center rounded-full text-white text-xs transition-transform hover:scale-110 cursor-pointer ${s.color}`}
          >
            <i className={s.icon}></i>
          </a>
        ))}
        <button
          onClick={handleCopy}
          title="Copy link"
          className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs transition-transform hover:scale-110 cursor-pointer relative"
        >
          <AnimatePresence mode="wait">
            {copied ? (
              <motion.i key="check" className="ri-check-line text-green-600" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} />
            ) : (
              <motion.i key="copy" className="ri-link-m" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} />
            )}
          </AnimatePresence>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Share:</span>
      {shareLinks.map((s) => (
        <a
          key={s.id}
          href={s.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-semibold transition-all hover:scale-105 cursor-pointer whitespace-nowrap ${s.color}`}
        >
          <i className={`${s.icon} text-sm`}></i>
          {s.label}
        </a>
      ))}
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
      >
        <AnimatePresence mode="wait">
          {copied ? (
            <motion.span key="copied" className="flex items-center gap-1 text-green-600" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <i className="ri-check-line"></i> Copied!
            </motion.span>
          ) : (
            <motion.span key="copy" className="flex items-center gap-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <i className="ri-link-m"></i> Copy Link
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}
